"use client";

import Link from "next/link";
import { useMemo } from "react";
import { useSearchParams } from "next/navigation";
import BlogCard from "./BlogCard";
import PublishBlogButton from "./PublishBlogButton";
import CategoryBar from "../CategoryBar";

const normalizeCategoryValue = (value) => String(value || "").trim().toLowerCase();
const parseArrayLikeString = (value) => {
  if (typeof value !== "string") return null;
  let current = value.trim();
  for (let i = 0; i < 2; i += 1) {
    if (!(current.startsWith("[") && current.endsWith("]"))) break;
    try {
      const parsed = JSON.parse(current);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "string") {
        current = parsed.trim();
        continue;
      }
    } catch {
      break;
    }
  }
  return null;
};
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  const parsed = parseArrayLikeString(value);
  if (parsed) return parsed;
  return [value];
};

const BlogListing = ({ blogs = [] }) => {
  const searchParams = useSearchParams();
  const selectedCategory = useMemo(() => {
    if (!searchParams) return "";
    const category = searchParams.get("category");
    return normalizeCategoryValue(category);
  }, [searchParams]);
  const selectedSubCategory = useMemo(() => {
    if (!searchParams) return "";
    const subCategory = searchParams.get("sub_category");
    return normalizeCategoryValue(subCategory);
  }, [searchParams]);

  const categories = useMemo(() => {
    if (!Array.isArray(blogs)) return [];

    const deduped = new Map();
    blogs.forEach((blog) => {
      const rawCategories = toArray(blog?.category).map((item) => String(item || "").trim());
      rawCategories.forEach((rawCategory) => {
        const normalizedCategory = normalizeCategoryValue(rawCategory);
        if (!normalizedCategory || deduped.has(normalizedCategory)) return;
        deduped.set(normalizedCategory, {
          value: normalizedCategory,
          label: rawCategory || normalizedCategory,
        });
      });
    });

    return Array.from(deduped.values());
  }, [blogs]);

  const selectedCategoryLabel = useMemo(() => {
    if (!selectedCategory) return "";
    const matchedCategory = categories.find(
      (category) => category.value === selectedCategory
    );
    return matchedCategory?.label || selectedCategory;
  }, [categories, selectedCategory]);

  const blogsByCategory = useMemo(() => {
    if (!selectedCategory) return blogs;
    return blogs.filter(
      (blog) => {
        const categories = toArray(blog?.category).map((item) =>
          normalizeCategoryValue(item)
        );
        return categories.includes(selectedCategory);
      }
    );
  }, [blogs, selectedCategory]);
  const subCategories = useMemo(() => {
    const deduped = new Map();
    blogsByCategory.forEach((blog) => {
      const rawSubCategories = toArray(
        blog?.sub_category || blog?.subCategory
      ).map((item) => String(item || "").trim());
      rawSubCategories.forEach((rawSubCategory) => {
        const normalizedSubCategory = normalizeCategoryValue(rawSubCategory);
        if (!normalizedSubCategory || deduped.has(normalizedSubCategory)) return;
        deduped.set(normalizedSubCategory, {
          value: normalizedSubCategory,
          label: rawSubCategory || normalizedSubCategory,
        });
      });
    });
    return Array.from(deduped.values());
  }, [blogsByCategory]);

  const selectedSubCategoryLabel = useMemo(() => {
    if (!selectedSubCategory) return "";
    const matchedSubCategory = subCategories.find(
      (subCategory) => subCategory.value === selectedSubCategory
    );
    return matchedSubCategory?.label || selectedSubCategory;
  }, [selectedSubCategory, subCategories]);

  const filteredBlogs = useMemo(() => {
    if (!selectedSubCategory) return blogsByCategory;
    return blogsByCategory.filter(
      (blog) => {
        const subCategories = toArray(blog?.sub_category || blog?.subCategory).map(
          (item) => normalizeCategoryValue(item)
        );
        return subCategories.includes(selectedSubCategory);
      }
    );
  }, [blogsByCategory, selectedSubCategory]);

  return (
    <section className="w-full py-20 lg:py-28 px-4 lg:px-20">
      <div className="flex flex-col items-center gap-16">
        <div className="flex flex-col items-center gap-6 lg:gap-8 max-w-3xl text-center">
          {selectedCategory && (
            <nav className="flex items-center gap-2 text-sm text-gray-500">
              <Link
                href="/blog"
                className="hover:text-primary transition-colors"
              >
                Blogs
              </Link>
              <span className="opacity-50">{'>'}</span>
              <span className="text-gray-700 font-medium tracking-wide">
                {selectedCategoryLabel}
              </span>
              {selectedSubCategory ? (
                <>
                  <span className="opacity-50">{'>'}</span>
                  <span className="text-gray-700 font-medium tracking-wide">
                    {selectedSubCategoryLabel}
                  </span>
                </>
              ) : null}
            </nav>
          )}

          <h1 className="text-4xl md:text-5xl font-semibold text-gray-900 tracking-tight">
            Our Blogs
          </h1>

          <p className="text-lg md:text-xl text-gray-500">
            Insights and stories from the world of finance.
          </p>

          <CategoryBar
            categories={categories}
            activeCategory={selectedCategory}
            className="mt-2"
          />
          {selectedCategory && subCategories.length > 0 ? (
            <nav className="w-full mt-2">
              <ul className="flex flex-wrap justify-center gap-3">
                <li key="all-sub-categories">
                  <Link
                    href={`/blog?category=${encodeURIComponent(selectedCategory)}`}
                    className={`inline-flex px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                      !selectedSubCategory
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-gray-700 border-gray-200 hover:border-primary hover:text-primary"
                    }`}
                  >
                    All Sub-categories
                  </Link>
                </li>
                {subCategories.map((subCategory) => (
                  <li key={subCategory.value}>
                    <Link
                      href={`/blog?category=${encodeURIComponent(
                        selectedCategory
                      )}&sub_category=${encodeURIComponent(subCategory.value)}`}
                      className={`inline-flex px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                        selectedSubCategory === subCategory.value
                          ? "bg-primary text-white border-primary"
                          : "bg-white text-gray-700 border-gray-200 hover:border-primary hover:text-primary"
                      }`}
                    >
                      {subCategory.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}

          <PublishBlogButton />
        </div>

        <div className="w-full max-w-6xl">
          {filteredBlogs.length === 0 ? (
            <p className="text-center text-gray-500 py-16">
              No blogs found for this category.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog, idx) => (
                <Link href={`/blog/${blog.id}`} key={idx} className="group">
                  <BlogCard
                    {...blog}
                    sub_category={blog?.sub_category || blog?.subCategory}
                  />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default BlogListing;
